import { ComponentType, useEffect } from "react";
import { config } from "../../auth.config";
import { Loader } from "../components/Loader";
import { useSession } from "./hooks";

type SessionProviderProps = {
  children: React.ReactNode;
};

/**
 * Wraps a route to ensure user is authed. Shows a loading screen
 * when loading, redirects to login if not authed.
 */
export const SessionProvider = ({ children }: SessionProviderProps): React.ReactElement | null => {
  const { isLoading, data } = useSession();

  /**
   * Redirect the user to a page with a redirect
   * query param in case they're not authed
   */
  const redirect = (base: string) => {
    window.location.replace(
      `${base}/login?${
        new URLSearchParams({
          redirect: window.location.href,
        }).toString()
      }`,
    );
  };

  /**
   * Checks which environment the user is on and redirects
   * accordingly
   */
  useEffect(() => {
    if (!isLoading && !data) {
      if (config.PUBLIC_AUTH_API.includes("mojomortgages.com")) {
        redirect(`https://mymojo.mojomortgages.com`);
      } else if (config.PUBLIC_AUTH_API.includes("mojo.xyz")) {
        redirect(`https://mymojo.test.mojo.xyz`);
      } else {
        redirect(`http://localhost:1061`);
      }
    }
  }, [isLoading, data]);

  if (isLoading || !data) return <Loader />;

  return <>{children}</>;
};

export const withSessionProvider = <P extends object>(
  WrappedComponent: ComponentType<P>,
) => {
  return function WithSessionProvider(props: P) {
    return (
      <SessionProvider>
        <WrappedComponent {...props} />
      </SessionProvider>
    );
  };
};
